import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SideBar from '../usercomponents/SideBar';
import TopNavBar from '../usercomponents/TopNavBar';
import Footer from '../usercomponents/Footer';

const apiUrl = process.env.REACT_APP_API_URL;

const STATUS_LABEL = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed: 'Completed',
  attempts_exhausted: 'Attempts Exhausted',
};

function MockExamList() {
  const navigate = useNavigate();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(null);

  const getToken = () => JSON.parse(sessionStorage.getItem('studentSession'))?.token;

  const fetchExams = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getToken();
      if (!token) { setError('Authentication required.'); setLoading(false); return; }
      const response = await axios.get(`${apiUrl}/api/student/mock-exams`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) setExams(response.data.exams || []);
      else setError(response.data.message || 'Failed to fetch exams.');
    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching exams.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExams(); }, [fetchExams]);

  const handleStartOrContinue = async (exam) => {
    setError('');
    setStarting(exam.id);
    try {
      const token = getToken();
      const response = await axios.post(`${apiUrl}/api/student/mock-exams/${exam.id}/attempts`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.success) {
        navigate(`/mock-exam-take/${response.data.attemptId}`);
      } else {
        setError(response.data.message || 'Could not start the exam.');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error starting exam.');
    } finally {
      setStarting(null);
    }
  };

  return (
    <>
      <SideBar />
      <div className="dashboard-main-wrapper">
        <TopNavBar />
        <div className="dashboard-body px-3">
          <div className="breadcrumb mb-24">
            <ul className="d-flex align-items-center gap-2 list-unstyled">
              <li><Link to="/dashboard" className="text-gray-200 text-15 hover-text-main-600 text-decoration-none">Home</Link></li>
              <li><span className="text-gray-500 d-flex align-items-center"><i className="ph ph-caret-right" /></span></li>
              <li><span className="text-main-600 text-15">Mock Exams</span></li>
            </ul>
          </div>

          {error && <div className="alert alert-danger mb-20">{error}</div>}

          {loading ? (
            <div className="text-center py-40"><div className="spinner-border text-main-600" /></div>
          ) : exams.length === 0 ? (
            <div className="card shadow-sm border-0"><div className="card-body text-center py-40 text-gray-400">
              No mock exams are available for your enrolled courses yet.
            </div></div>
          ) : (
            <div className="row g-16">
              {exams.map((exam) => {
                const isNotYetAvailable = exam.availability === 'not_yet_available';
                const isExpired = exam.availability === 'expired';
                const canStart = !isNotYetAvailable && !isExpired && exam.attempt_status !== 'attempts_exhausted';
                return (
                  <div className="col-md-6" key={exam.id}>
                    <div className="card shadow-sm border-0 h-100">
                      <div className="card-body p-20">
                        <div className="fw-bold text-16 mb-4">{exam.title}</div>
                        <div className="text-13 text-gray-500 mb-12">{exam.coursename}</div>
                        <div className="d-flex flex-wrap gap-16 text-13 text-gray-500 mb-12">
                          <span><i className="ph ph-list-checks me-1" />{exam.total_questions} Questions</span>
                          <span><i className="ph ph-star me-1" />{exam.estimated_total_marks} Marks (approx.)</span>
                          <span><i className="ph ph-flag me-1" />Pass: {exam.pass_percentage}%</span>
                          {exam.duration_minutes && <span><i className="ph ph-clock me-1" />{exam.duration_minutes} min</span>}
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <span className="badge bg-main-50 text-main-600">{STATUS_LABEL[exam.attempt_status] || exam.attempt_status}</span>
                            {exam.attempts_remaining !== null && (
                              <span className="text-13 text-gray-500 ms-8">{exam.attempts_remaining} attempt(s) left</span>
                            )}
                            {isNotYetAvailable && <span className="text-13 text-warning ms-8">Not yet available</span>}
                            {isExpired && <span className="text-13 text-danger ms-8">Expired</span>}
                          </div>
                          {exam.attempt_status === 'in_progress' ? (
                            <button type="button" className="btn btn-main rounded-pill py-6 px-16" onClick={() => handleStartOrContinue(exam)} disabled={starting === exam.id}>
                              {starting === exam.id ? '...' : 'Continue'}
                            </button>
                          ) : exam.completed_attempts > 0 ? (
                            <div className="d-flex gap-8">
                              <Link to={`/mock-exam-history`} className="btn btn-outline-main rounded-pill py-6 px-16">View Result</Link>
                              {canStart && (
                                <button type="button" className="btn btn-main rounded-pill py-6 px-16" onClick={() => navigate(`/mock-exam-instructions/${exam.id}`)}>
                                  Retake
                                </button>
                              )}
                            </div>
                          ) : (
                            <button type="button" className="btn btn-main rounded-pill py-6 px-16" onClick={() => navigate(`/mock-exam-instructions/${exam.id}`)} disabled={!canStart}>
                              Start Exam
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </>
  );
}

export default MockExamList;
